import type { Permission } from './permissions.js';

/**
 * Berichtskatalog (Quelle der Wahrheit für API, Web und App). Jeder Eintrag beschreibt einen herunterladbaren Bericht:
 * auf welcher Ebene er liegt, wer ihn sehen darf und unter welchem Pfad er geladen wird. Die API registriert genau eine
 * Route je Eintrag (Test), die Web-App zeigt nur Berichte, die die Person laut Rechten laden darf. Die Rechte prüft
 * immer der Server; die Anzeige im Web ist nur eine Hilfe.
 *
 * `permission`:
 * - ein Recht aus `permissions.ts` (Organisation bzw. Basar aus dem Pfad),
 * - `self`: eigene Daten einer angemeldeten Person (Verkäufer), die Eigentumsprüfung macht die API,
 * - `platform`: nur Plattform-Admins.
 *
 * `personal`: Der Bericht enthält Namen oder Kontaktdaten. Bei Basar-Berichten werden diese Spalten ohne das Recht
 * `event.contacts.view` leer ausgegeben (der Bericht selbst bleibt mit `event.reports.export` ladbar).
 */
export const REPORT_LEVELS = ['platform', 'org', 'event', 'seller'] as const;
export type ReportLevel = (typeof REPORT_LEVELS)[number];

export interface ReportDefinition {
  readonly id: string;
  readonly level: ReportLevel;
  readonly permission: Permission | 'self' | 'platform';
  readonly title: string;
  readonly description: string;
  readonly format: 'xlsx' | 'pdf';
  readonly personal: boolean;
  /** Pfad unter `/api/v1` mit `:orgId`, `:eventId` bzw. `:registrationId`. */
  readonly path: string;
  /**
   * Druckansicht (JSON mit denselben Blättern wie die Excel-Datei, Seite im Web unter `/…/druck`): gleiche Rechte und
   * Mandantentrennung wie `path`. Fehlt, wenn es keine Druckansicht gibt.
   */
  readonly printPath?: string;
}

/** Berichte der Basar-Ebene: `:report` in `/events/:eventId/reports/:report/xlsx`. */
export const EVENT_REPORT_IDS = [
  'sellers',
  'items',
  'sales',
  'cash',
  'conflicts',
  'settlement',
  'payouts',
  'helpers',
] as const;
export type EventReportId = (typeof EVENT_REPORT_IDS)[number];

/** Basar-Berichte mit Druckansicht (Route `…/reports/:report/print`); die übrigen Berichte drucken als eigene Seite. */
export const PRINT_REPORT_IDS = ['sellers', 'items', 'sales', 'cash', 'conflicts'] as const;
export type PrintReportId = (typeof PRINT_REPORT_IDS)[number];

const eventReport = (
  id: EventReportId,
  title: string,
  description: string,
  personal: boolean,
): ReportDefinition => ({
  id,
  level: 'event',
  permission: 'event.reports.export',
  title,
  description,
  format: 'xlsx',
  personal,
  path: `/events/:eventId/reports/${id}/xlsx`,
  ...((PRINT_REPORT_IDS as readonly string[]).includes(id)
    ? { printPath: `/events/:eventId/reports/${id}/print` }
    : {}),
});

export const REPORTS = [
  eventReport(
    'sellers',
    'Verkäufer',
    'Nummer, Status, Artikel, Umsatz und Auszahlung je Verkäufer.',
    true,
  ),
  eventReport('items', 'Artikel', 'Alle Artikel mit Status, Verkaufszeit und Kasse.', false),
  eventReport('sales', 'Verkaufsjournal', 'Jeder einzelne Verkauf, chronologisch.', false),
  eventReport(
    'cash',
    'Kassen und Verkaufsübersicht',
    'Verkaufsübersicht je Kasse und Tag: bar, Anzahl, Stornos, Konflikte.',
    true,
  ),
  eventReport(
    'conflicts',
    'Konflikte',
    'Doppelte Verkäufe und Preisabweichungen zur Klärung.',
    true,
  ),
  eventReport('settlement', 'Abrechnung', 'Abrechnung je Verkäufer und gesamt.', true),
  eventReport('payouts', 'Auszahlungsliste', 'Liste zum Abhaken bei der Barauszahlung.', true),
  eventReport('helpers', 'Helferliste', 'Alle Helfer mit Kontaktdaten und Status.', true),
  {
    id: 'not-offered',
    level: 'event',
    permission: 'event.contacts.view',
    title: 'Lange nicht angeboten',
    description:
      'Frühere Verkäufer der Organisation, die lange nicht mehr dabei waren und im Basar fehlen. Mit Namen und E-Mail.',
    format: 'xlsx',
    personal: true,
    path: '/events/:eventId/not-offered.xlsx',
    printPath: '/events/:eventId/not-offered/print',
  },
  {
    id: 'org-members',
    level: 'org',
    permission: 'org.members.manage',
    title: 'Mitglieder und Rollen',
    description: 'Name, E-Mail, Rolle und Mitglied seit (ohne Telefonnummern).',
    format: 'xlsx',
    personal: true,
    path: '/orgs/:orgId/reports/members.xlsx',
    printPath: '/orgs/:orgId/reports/members/print',
  },
  {
    id: 'org-events',
    level: 'org',
    permission: 'org.reports.export',
    title: 'Basar-Vergleich',
    description:
      'Je Basar: Status, Zeitraum, Verkäufer, Artikel, Verkaufssumme und Provision nebeneinander.',
    format: 'xlsx',
    personal: false,
    path: '/orgs/:orgId/reports/events.xlsx',
    printPath: '/orgs/:orgId/reports/events/print',
  },
  {
    id: 'org-sellers-across',
    level: 'org',
    permission: 'org.members.manage',
    title: 'Verkäufer über mehrere Basare',
    description:
      'Je Verkäufer: Anzahl der Teilnahmen, Umsatz je Basar und zuletzt dabei. Mit Namen und E-Mail.',
    format: 'xlsx',
    personal: true,
    path: '/orgs/:orgId/reports/sellers-across.xlsx',
    printPath: '/orgs/:orgId/reports/sellers-across/print',
  },
  {
    id: 'org-fees',
    level: 'org',
    permission: 'org.reports.export',
    title: 'Gebührenübersicht je Basar',
    description:
      'Je Basar: Verkaufssumme, Provision, Verkäufergebühr und die Einnahmen daraus (ohne Auszahlungen).',
    format: 'xlsx',
    personal: false,
    path: '/orgs/:orgId/reports/fees.xlsx',
    printPath: '/orgs/:orgId/reports/fees/print',
  },
  {
    id: 'org-returning',
    level: 'org',
    permission: 'org.reports.export',
    title: 'Beteiligung und Wiederkehrer',
    description:
      'Je Basar: freigegebene Verkäufer und wie viele davon schon bei früheren Basaren der Organisation dabei waren.',
    format: 'xlsx',
    personal: false,
    path: '/orgs/:orgId/reports/returning.xlsx',
    printPath: '/orgs/:orgId/reports/returning/print',
  },
  {
    id: 'seller-items',
    level: 'seller',
    permission: 'self',
    title: 'Meine Artikelliste',
    description:
      'Alle eigenen Artikel mit Nummer, Preis, Status, BARSO-Code und übernommenem Etikett.',
    format: 'xlsx',
    personal: false,
    path: '/me/registrations/:registrationId/items.xlsx',
  },
  {
    id: 'seller-receipt',
    level: 'seller',
    permission: 'self',
    title: 'Meine Abrechnung',
    description: 'Abrechnungsbeleg als PDF, sobald der Veranstalter abgerechnet hat.',
    format: 'pdf',
    personal: false,
    path: '/me/registrations/:registrationId/settlement/receipt.pdf',
  },
  {
    id: 'platform-licenses',
    level: 'platform',
    permission: 'platform',
    title: 'Lizenzen und Lizenzumsatz',
    description: 'Gebuchte Lizenzen und Umsatz je Monat (keine Verkaufszahlen der Basare).',
    format: 'xlsx',
    personal: false,
    path: '/platform/reports/licenses.xlsx',
  },
] as const satisfies readonly ReportDefinition[];

export type ReportId = (typeof REPORTS)[number]['id'];

export function reportsOfLevel(level: ReportLevel): readonly ReportDefinition[] {
  return REPORTS.filter((report) => report.level === level);
}

/** Was eine Person auf einer Ebene darf (aus den Rechten dieser Organisation bzw. dieses Basars aufgelöst). */
export interface ReportGrants {
  readonly permissions: readonly Permission[];
  /** Eigene Daten (Verkäufer-Ebene). */
  readonly self?: boolean;
  /** Plattform-Admin. */
  readonly platform?: boolean;
}

/** Berichte einer Ebene, die diese Person laden darf. Leer = keine Kachel „Berichte“ zeigen. */
export function visibleReports(
  level: ReportLevel,
  grants: ReportGrants,
): readonly ReportDefinition[] {
  return reportsOfLevel(level).filter((report) => {
    if (report.permission === 'self') return grants.self === true;
    if (report.permission === 'platform') return grants.platform === true;
    return grants.permissions.includes(report.permission);
  });
}

/** Setzt die Kennungen in das Pfad-Muster ein (jede Kennung wird als Pfadteil kodiert). */
export function reportPath(
  report: ReportDefinition,
  ids: { orgId?: string; eventId?: string; registrationId?: string } = {},
): string {
  return report.path.replace(/:(orgId|eventId|registrationId)/g, (_match, name: string) => {
    const value = ids[name as 'orgId' | 'eventId' | 'registrationId'];
    if (value === undefined) throw new TypeError(`Kennung ${name} fehlt für ${report.id}.`);
    return encodeURIComponent(value);
  });
}

/**
 * Berichte-Seite eines Basars als Kacheln in fünf Gruppen (Plan `docs/12-PLAN-BERICHTE-AUSWERTUNGEN.md`).
 * Eine Kachel ist entweder eine In-App-Auswertung (`view`: Unterroute `/basare/:eventId/berichte/:id`, Recht
 * `event.reports.view`, auch für Mitglieder) oder ein Excel-Bericht aus `REPORTS` (`excel`, Recht `event.reports.export`),
 * ein Eintrag darf beides haben. `compare` verweist auf den Vergleich der Organisation (nur mit `org.reports.export`).
 * Die Rechte prüft immer der Server; der Katalog steuert nur die Anzeige.
 */
export const ANALYSIS_GROUPS = ['sales', 'goods', 'sellers', 'team', 'wrapup'] as const;
export type AnalysisGroup = (typeof ANALYSIS_GROUPS)[number];

export const ANALYSIS_GROUP_TITLES: Readonly<Record<AnalysisGroup, string>> = {
  sales: 'Verkauf',
  goods: 'Ware',
  sellers: 'Verkäufer',
  team: 'Team',
  wrapup: 'Abschluss',
};

export interface EventAnalysisDefinition {
  /** Kennung und Pfadteil (`/basare/:eventId/berichte/<id>`). */
  readonly id: string;
  readonly group: AnalysisGroup;
  readonly title: string;
  /** Ein Satz Kurzinfo für die Kachel. */
  readonly description: string;
  /** In-App-Auswertung vorhanden. */
  readonly view: boolean;
  /** Excel-Bericht dazu (`EVENT_REPORT_IDS`). */
  readonly excel?: EventReportId;
  /** Verweis auf den Vergleich der Organisation. */
  readonly compare?: boolean;
  /**
   * Druckansicht unter `/basare/:eventId/berichte/<print>/druck`: Kennung eines Basar-Berichts aus `REPORTS`
   * (Recht laut Katalog, für „Lange nicht angeboten“ das Recht für Kontaktdaten).
   */
  readonly print?: string;
}

export const EVENT_ANALYSES: readonly EventAnalysisDefinition[] = [
  {
    id: 'verkaufsuebersicht',
    group: 'sales',
    title: 'Verkaufsübersicht',
    description: 'Umsatz, Verkäufe und durchschnittlicher Warenkorb.',
    view: true,
  },
  {
    id: 'stunden',
    group: 'sales',
    title: 'Verkäufe je Stunde',
    description: 'Wann am meisten los war, je Basartag.',
    view: true,
  },
  {
    id: 'preisspannen',
    group: 'sales',
    title: 'Preisspannen',
    description: 'Anzahl und Umsatz je Preisspanne.',
    view: true,
  },
  {
    id: 'verkaufsjournal',
    group: 'sales',
    title: 'Verkaufsjournal',
    description: 'Jeder einzelne Verkauf, chronologisch.',
    view: false,
    excel: 'sales',
    print: 'sales',
  },
  {
    id: 'kategorien',
    group: 'goods',
    title: 'Kategorien',
    description: 'Verkaufsquote je Kategorie mit den stärksten und schwächsten.',
    view: true,
  },
  {
    id: 'groessen',
    group: 'goods',
    title: 'Größen',
    description: 'Verkaufsquote je Größe.',
    view: true,
  },
  {
    id: 'preise',
    group: 'goods',
    title: 'Ø-Preis: verkauft und angenommen',
    description: 'Durchschnitt und Median, gesamt und je Kategorie.',
    view: true,
  },
  {
    id: 'annahme',
    group: 'goods',
    title: 'Annahme und Rückgabe',
    description: 'Von der Anlage bis zum Verkauf oder zur Rückgabe.',
    view: true,
  },
  {
    id: 'artikel',
    group: 'goods',
    title: 'Artikel',
    description: 'Alle Artikel mit Status, Verkaufszeit und Kasse.',
    view: false,
    excel: 'items',
    print: 'items',
  },
  {
    id: 'verkaeufer-verteilung',
    group: 'sellers',
    title: 'Verteilung nach Verkaufsquote',
    description: 'Wie viele Verkäufer wie viel verkauft haben, anonym.',
    view: true,
  },
  {
    id: 'verkaeufer',
    group: 'sellers',
    title: 'Verkäufer',
    description: 'Nummer, Status, Artikel, Umsatz und Auszahlung je Verkäufer.',
    view: false,
    excel: 'sellers',
    print: 'sellers',
  },
  {
    id: 'lange-nicht-angeboten',
    group: 'sellers',
    title: 'Lange nicht angeboten',
    description: 'Frühere Verkäufer, die lange nicht mehr dabei waren. Mit Namen und E-Mail.',
    view: false,
    print: 'not-offered',
  },
  {
    id: 'kassen',
    group: 'team',
    title: 'Kassen-Leistung',
    description: 'Verkäufe, Umsatz und Ø-Warenkorb je Kasse.',
    view: true,
  },
  {
    id: 'helfer',
    group: 'team',
    title: 'Helfer und Schichten',
    description: 'Belegung und Anwesenheit je Schicht.',
    view: true,
  },
  {
    id: 'helferliste',
    group: 'team',
    title: 'Helferliste',
    description: 'Alle Helfer mit Kontaktdaten und Status.',
    view: false,
    excel: 'helpers',
  },
  {
    id: 'kassen-tagesabschluss',
    group: 'team',
    title: 'Kassen und Verkaufsübersicht',
    description: 'Verkaufsübersicht je Kasse und Tag: bar, Anzahl, Stornos, Konflikte.',
    view: false,
    excel: 'cash',
    print: 'cash',
  },
  {
    id: 'konflikte',
    group: 'team',
    title: 'Konflikte',
    description: 'Doppelte Verkäufe und Preisabweichungen zur Klärung.',
    view: false,
    excel: 'conflicts',
    print: 'conflicts',
  },
  {
    id: 'erkenntnisse',
    group: 'wrapup',
    title: '3 Erkenntnisse',
    description: 'Die auffälligsten Zahlen in drei Sätzen.',
    view: true,
  },
  {
    id: 'abrechnung',
    group: 'wrapup',
    title: 'Abrechnung',
    description: 'Abrechnung je Verkäufer und gesamt.',
    view: false,
    excel: 'settlement',
  },
  {
    id: 'auszahlungsliste',
    group: 'wrapup',
    title: 'Auszahlungsliste',
    description: 'Liste zum Abhaken bei der Barauszahlung.',
    view: false,
    excel: 'payouts',
  },
  {
    id: 'vergleich',
    group: 'wrapup',
    title: 'Vergleich mit früheren Basaren',
    description: 'Umsatz, Quote und Kategorien über mehrere Basare.',
    view: false,
    compare: true,
  },
];

/** Kachel eines Basars, gefiltert nach Rechten: Ansehen mit `event.reports.view`, Excel mit `event.reports.export`. */
export interface VisibleAnalysis {
  readonly definition: EventAnalysisDefinition;
  readonly canView: boolean;
  readonly canExcel: boolean;
  readonly canCompare: boolean;
  /** Druckansicht der Kachel (`definition.print`) mit dem Recht des Katalogs. */
  readonly canPrint: boolean;
}

export function visibleAnalyses(grants: ReportGrants): readonly VisibleAnalysis[] {
  const has = (permission: Permission): boolean => grants.permissions.includes(permission);
  const result: VisibleAnalysis[] = [];
  for (const definition of EVENT_ANALYSES) {
    const canView = definition.view && has('event.reports.view');
    const canExcel = definition.excel !== undefined && has('event.reports.export');
    const canCompare = definition.compare === true && has('org.reports.export');
    // Druckansicht: das Recht des Katalogeintrags (Basar-Bericht oder „Lange nicht angeboten“)
    const printPermission = REPORTS.find(
      (report) => report.level === 'event' && report.id === definition.print,
    )?.permission;
    const canPrint =
      printPermission !== undefined &&
      printPermission !== 'self' &&
      printPermission !== 'platform' &&
      has(printPermission);
    if (canView || canExcel || canCompare || canPrint) {
      result.push({ definition, canView, canExcel, canCompare, canPrint });
    }
  }
  return result;
}
